// ═══════════════════════════════════════════════════════════════════════════════════
// [FAMLA-APP-COMPLETE] BRANDING STYLES HELPER - TENANT COLOR UTILITIES FOR TSX
// ═══════════════════════════════════════════════════════════════════════════════════
//
// [RÔLE] Fournir des helpers pour les styles inline TSX avec couleurs tenant
// [OBJECTIF] Remplacer les couleurs hardcodées (#c7755c) dans les composants
// [ARCHITECTURE] Fonctions pures utilisant tenantBranding de config.ts
//
// ═══════════════════════════════════════════════════════════════════════════════════

import { tenantBranding } from '../services/config';

/**
 * Convertit une couleur hex en RGB string
 */
export function hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '199, 117, 92';
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
}

/**
 * Retourne la couleur primaire du tenant
 */
export function getBrandingColor(): string {
    return tenantBranding.primaryColor;
}

/**
 * Retourne la couleur primaire du tenant avec une opacité donnée
 */
export function getBrandingColorRgba(alpha: number): string {
    const rgb = hexToRgb(tenantBranding.primaryColor);
    return `rgba(${rgb}, ${alpha})`;
}

/**
 * Retourne une couleur plus foncée pour le hover
 */
export function getBrandingColorHover(): string {
    const hex = tenantBranding.primaryColor.replace('#', '');
    const r = Math.max(0, parseInt(hex.slice(0, 2), 16) - 25);
    const g = Math.max(0, parseInt(hex.slice(2, 4), 16) - 25);
    const b = Math.max(0, parseInt(hex.slice(4, 6), 16) - 25);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Retourne un objet de styles inline pour un bouton primaire
 */
export function getBrandingButtonStyle(): React.CSSProperties {
    return {
        backgroundColor: tenantBranding.primaryColor,
        color: '#ffffff',
        borderColor: tenantBranding.primaryColor,
    };
}

/**
 * Retourne un objet de styles inline pour un bouton primaire au hover
 */
export function getBrandingButtonHoverStyle(): React.CSSProperties {
    return {
        backgroundColor: getBrandingColorHover(),
        color: '#ffffff',
        borderColor: getBrandingColorHover(),
    };
}

/**
 * Retourne un objet de styles inline pour un fond avec opacité
 */
export function getBrandingBackgroundStyle(alpha: number = 0.12): React.CSSProperties {
    return {
        backgroundColor: getBrandingColorRgba(alpha),
    };
}

/**
 * Retourne un objet de styles inline pour une bordure avec opacité
 */
export function getBrandingBorderStyle(alpha: number = 0.35): React.CSSProperties {
    return {
        borderColor: getBrandingColorRgba(alpha),
    };
}

/**
 * Retourne un objet de styles inline pour un texte avec la couleur tenant
 */
export function getBrandingTextStyle(): React.CSSProperties {
    return {
        color: tenantBranding.primaryColor,
    };
}

/**
 * Retourne un objet de styles inline pour un badge (fond soft + texte accent)
 */
export function getBrandingBadgeStyle(): React.CSSProperties {
    return {
        backgroundColor: getBrandingColorRgba(0.12),
        color: tenantBranding.primaryColor,
        borderColor: getBrandingColorRgba(0.35),
    };
}

/**
 * Retourne un objet de styles inline pour un avatar (fond soft + texte accent)
 */
export function getBrandingAvatarStyle(): React.CSSProperties {
    return {
        backgroundColor: getBrandingColorRgba(0.14),
        color: tenantBranding.primaryColor,
        borderColor: getBrandingColorRgba(0.35),
    };
}

/**
 * Retourne le gradient tenant
 */
export function getBrandingGradient(): string {
    return tenantBranding.gradient;
}

/**
 * Retourne la première lettre du nom de l'application tenant (pour les avatars)
 */
export function getTenantInitial(): string {
    return tenantBranding.appName.charAt(0).toUpperCase();
}

/**
 * Retourne le nom de l'application tenant
 */
export function getTenantName(): string {
    return tenantBranding.appName;
}

/**
 * Retourne un objet de styles inline pour un fond dégradé
 */
export function getBrandingGradientStyle(): React.CSSProperties {
    return {
        background: tenantBranding.gradient,
    };
}

export default {
    getBrandingColor,
    getBrandingColorRgba,
    getBrandingColorHover,
    getBrandingButtonStyle,
    getBrandingButtonHoverStyle,
    getBrandingBackgroundStyle,
    getBrandingBorderStyle,
    getBrandingTextStyle,
    getBrandingBadgeStyle,
    getBrandingAvatarStyle,
    getBrandingGradient,
    getBrandingGradientStyle,
    getTenantInitial,
    getTenantName,
    hexToRgb,
};
